/**
 * Grouped validation summary with entity-level remediation actions.
 */

import type {
  ProtocolCriterion,
  ProtocolValidationFinding,
  ProtocolValidationRun,
} from "../../api/clinicalTrialsApiTypes";
import { CriterionRationaleActions } from "./CriterionRationaleActions";
import {
  groupValidationFindings,
  type ProtocolFindingGroup,
} from "./protocolValidationFindings";

type ProtocolValidationPanelProps = {
  validation: ProtocolValidationRun;
  criteria: ProtocolCriterion[];
  busy: boolean;
  onSaveRationale: (
    criterion: ProtocolCriterion,
    rationale: string,
  ) => Promise<void>;
};

/**
 * Resolve affected criteria for a finding group.
 *
 * @param findings - Grouped validation findings
 * @param criteria - Draft criteria
 * @returns Criteria referenced by the findings
 */
function affectedCriteria(
  findings: ProtocolValidationFinding[],
  criteria: ProtocolCriterion[],
): ProtocolCriterion[] {
  const ids = new Set(
    findings
      .filter((finding) => finding.entity_type === "criterion")
      .map((finding) => finding.entity_id),
  );
  return criteria.filter((criterion) => ids.has(criterion.criterion_id));
}

/**
 * Render one grouped validation finding and its available actions.
 *
 * @param props - Finding group display props
 * @returns Finding card
 */
function FindingGroupCard({
  group,
  severity,
  criteria,
  busy,
  onSaveRationale,
}: {
  group: ProtocolFindingGroup;
  severity: "error" | "warning";
  criteria: ProtocolCriterion[];
  busy: boolean;
  onSaveRationale: (
    criterion: ProtocolCriterion,
    rationale: string,
  ) => Promise<void>;
}) {
  const affected = affectedCriteria(group.findings, criteria);
  const colorClass =
    severity === "error"
      ? "border-red-200 bg-red-50 text-red-900"
      : "border-amber-200 bg-amber-50 text-amber-900";
  const entityIds = group.findings
    .map((finding) => finding.entity_id)
    .filter((entityId): entityId is string => Boolean(entityId));

  return (
    <li className={`rounded-lg border p-3 ${colorClass}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-medium">{group.title}</div>
          <p className="mt-1 text-xs opacity-80">{group.guidance}</p>
        </div>
        <span className="rounded-full bg-white/80 px-2 py-0.5 text-xs font-semibold">
          {group.findings.length} affected
        </span>
      </div>
      {group.code === "criterion.rationale_missing" ? (
        <CriterionRationaleActions
          criteria={affected}
          busy={busy}
          onSave={onSaveRationale}
        />
      ) : entityIds.length > 0 ? (
        <div className="mt-2 text-xs opacity-75">
          Affected: {entityIds.slice(0, 5).join(", ")}
          {entityIds.length > 5 ? ` and ${entityIds.length - 5} more` : ""}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Render grouped validation results with remediation guidance.
 *
 * @param props - Validation, draft criteria, and rationale save handler
 * @returns Actionable validation panel
 */
export function ProtocolValidationPanel({
  validation,
  criteria,
  busy,
  onSaveRationale,
}: ProtocolValidationPanelProps) {
  const errorGroups = groupValidationFindings(validation.errors);
  const warningGroups = groupValidationFindings(validation.warnings);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-semibold text-slate-900">Validation results</h2>
        <span className="text-xs text-slate-600">
          {validation.errors.length} errors · {validation.warnings.length}{" "}
          warnings
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Errors block a valid protocol. Warnings identify missing review metadata
        and should be resolved before publication.
      </p>
      <ul className="mt-3 space-y-3">
        {errorGroups.map((group) => (
          <FindingGroupCard
            key={group.key}
            group={group}
            severity="error"
            criteria={criteria}
            busy={busy}
            onSaveRationale={onSaveRationale}
          />
        ))}
        {warningGroups.map((group) => (
          <FindingGroupCard
            key={group.key}
            group={group}
            severity="warning"
            criteria={criteria}
            busy={busy}
            onSaveRationale={onSaveRationale}
          />
        ))}
      </ul>
    </section>
  );
}
